import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe, StripeEmbeddedCheckout } from "@stripe/stripe-js";

const clients = new Map<string, Promise<Stripe | null>>();

// The pure import never injects Stripe.js until hosted bootstrap supplies a key.
export function checkoutStripe(key: string): Promise<Stripe | null> {
  const existing = clients.get(key);
  if (existing) return existing;
  const promise = loadStripe(key).then(
    (stripe) => {
      if (!stripe) clients.delete(key);
      return stripe;
    },
    () => {
      clients.delete(key);
      return null;
    },
  );
  clients.set(key, promise);
  return promise;
}

// react-stripe-js 6.9 has no initialization error callback and does not catch
// its initialization promise. Keep that rejection inside this SDK boundary.
// The provider still owns mounting; this wrapper makes teardown idempotent,
// including an initialization that finishes after the route has unmounted.
export function checkoutStripeLifecycle(onFailure: () => void) {
  let disposed = false;
  let checkout: StripeEmbeddedCheckout | null = null;
  const destroy = () => {
    checkout?.destroy();
    checkout = null;
  };
  const lifecycle: StripeEmbeddedCheckout = {
    mount: (target) => {
      if (!disposed) checkout?.mount(target);
    },
    unmount: () => {
      checkout?.unmount();
    },
    destroy,
  };
  return {
    dispose: () => {
      disposed = true;
      destroy();
    },
    wrap: (stripe: Stripe): Stripe =>
      new Proxy(stripe, {
        get(target, property, receiver) {
          if (property !== "createEmbeddedCheckoutPage") {
            return Reflect.get(target, property, receiver) as unknown;
          }
          const initialize: Stripe["createEmbeddedCheckoutPage"] = async (
            options,
          ) => {
            try {
              checkout = await target.createEmbeddedCheckoutPage(options);
              if (disposed) destroy();
            } catch {
              if (!disposed) onFailure();
            }
            // On failure the payment component renders its error and unmounts
            // the provider. Its cleanup can safely destroy this empty handle.
            return lifecycle;
          };
          return initialize;
        },
      }),
  };
}
